
import { isWebGPUAvailable, isWebGPUSupported } from '../wgs/wgpu/Support.js';
import {
  GetGpuAdapterFeatures,
  GetGpuAdapterInfo,
  GetGpuAdapterLimits,
  IsCompatibilityMode,
  IsDegraded } from
'../wgs/wgpu/globals.js';

import { isFirefox, isChrome } from '../compat.js';
import { USE_WEBGPU } from '../wgs/globals.js';

/**
 * Query the WebGPU status, i.e., if its available by the browser and supported by LMV. If it enabled or not is tracked
 * by the Viewer and handled similar to other feature flags.
 *
 * @returns {Object} A payload for analytics.
 */
export async function queryWebGPUStatusPayload() {
  const payload = {
    // true if WebGPU is successfully enabled, i.e., both feature flag and toggle are on, and WebGPU was available.
    is_enabled_WebGPU: Boolean(USE_WEBGPU)
  };
  try {
    payload.is_available_WebGPU = await isWebGPUAvailable(); // track actual availability
    payload.is_supported_WebGPU = await isWebGPUSupported(); // track our support
  } catch (error) {
    console.debug('Exception thrown when checking WebGPU support.', error); // this should not bother end users
  }
  return payload;
}

/**
 * Internal helper to query common WebGPU adapter and screen info for analytics.
 * @returns {Object} A base payload for analytics.
 */
function queryCommonWebGPUAdapterPayload() {

  const info = GetGpuAdapterInfo();
  const payload = {
    screen_devicePixelRatio: window.devicePixelRatio,
    screen_nativeWidth: window.screen.width * window.devicePixelRatio,
    screen_nativeHeight: window.screen.height * window.devicePixelRatio,
    screen_colorDepth: window.screen.colorDepth,
    screen_orientation_type: window.screen.orientation?.type,
    hardwareConcurrency: navigator.hardwareConcurrency,

    gpu_adapter_info_vendor: info.vendor,
    gpu_adapter_info_architecture: info.architecture,
    gpu_adapter_info_description: info.description,
    gpu_adapter_info_isFallbackAdapter: info.isFallbackAdapter,

    webgpu_compatibility_mode: IsCompatibilityMode(),
    webgpu_degraded: IsDegraded()
  };
  return payload;
}

/**
 * Query screen info, WebGPU adapter info, features, limits, and optionally WebGL2 renderer details for analytics.
 *
 * If queryDebugRenderInfoFromWebGL is true, a temporary WebGL2 context is created to query the unmasked vendor and
 * renderer. There are two concerns with this:
 *
 *   1. adapter mismatch: In various scenarios, the WebGL2 context might not be on the same GPU as the WebGPU context.
 *      A mismatch could, however, would indicate availability of both integrated and dedicated GPUs, which is useful.
 *
 *   2. fingerprinting: Asking for the unmasked adapter name is a #1 GPU fingerprinting vector, but sadly it is the
 *      only way to even remotely get some data on client GPU capabilities. So we should consider to not have this
 *      always enabled.
 *
 * @param {Boolean} queryDebugRenderInfoFromWebGL Whether to query the unmasked vendor and renderer from a WebGL2 context.
 * @returns {Object} A payload for analytics.
 */
export function queryWebGPUAdapterPayload(queryDebugRenderInfoFromWebGL = false) {

  const payload = queryCommonWebGPUAdapterPayload();

  const features = GetGpuAdapterFeatures();
  const info = GetGpuAdapterInfo();
  const limits = GetGpuAdapterLimits();

  const featuresToTrack = Object.freeze({
    coreFeaturesAndLimits: 'core-features-and-limits',
    indirectFirstInstance: 'indirect-first-instance',
    multiDrawIndirect: isChrome() ? 'chromium-experimental-multi-draw-indirect' : 'multi-draw-indirect',
    timestampQuery: 'timestamp-query'
  });
  const limitsToTrack = Object.freeze([
  'maxBindGroups',
  'maxBindGroupsPlusVertexBuffers',
  'maxBindingsPerBindGroup',
  'maxBufferSize',
  'maxColorAttachmentBytesPerSample',
  'maxColorAttachments',
  'maxComputeInvocationsPerWorkgroup',
  'maxComputeWorkgroupSizeX',
  'maxComputeWorkgroupSizeY',
  'maxComputeWorkgroupSizeZ',
  'maxComputeWorkgroupStorageSize',
  'maxComputeWorkgroupsPerDimension',
  'maxDynamicStorageBuffersPerPipelineLayout',
  'maxDynamicUniformBuffersPerPipelineLayout',
  'maxInterStageShaderVariables',
  'maxSampledTexturesPerShaderStage',
  'maxSamplersPerShaderStage',
  'maxStorageBufferBindingSize',
  'maxStorageBuffersInFragmentStage',
  'maxStorageBuffersInVertexStage',
  'maxStorageBuffersPerShaderStage',
  'maxStorageTexturesInFragmentStage',
  'maxStorageTexturesInVertexStage',
  'maxStorageTexturesPerShaderStage',
  'maxTextureArrayLayers',
  'maxTextureDimension1D',
  'maxTextureDimension2D',
  'maxTextureDimension3D',
  'maxUniformBufferBindingSize',
  'maxUniformBuffersPerShaderStage',
  'maxVertexAttributes',
  'maxVertexBufferArrayStride',
  'maxVertexBuffers',
  'minStorageBufferOffsetAlignment',
  'minUniformBufferOffsetAlignment']
  );

  for (const [key, value] of Object.entries(featuresToTrack)) {
    payload[`gpu_adapter_feature_${key}`] = features.has(value);
  }
  for (const key of limitsToTrack) {
    payload[`gpu_adapter_limit_${key}`] = limits[key];
  }

  if (queryDebugRenderInfoFromWebGL === false) {
    return payload;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('webgl2');

  if (!(context instanceof WebGL2RenderingContext)) {
    return payload;
  }

  // The extension is deprecated in Firefox and is tagged for removal in the future.
  // - https://developer.mozilla.org/en-US/docs/Web/API/WEBGL_debug_renderer_info
  // - https://hg-edge.mozilla.org/integration/autoland/rev/52689741f6de
  const WEBGL_debug_renderer_info = isFirefox() ? undefined : context.getExtension('WEBGL_debug_renderer_info');

  const webglVendor = context.getParameter(WEBGL_debug_renderer_info ? WEBGL_debug_renderer_info.UNMASKED_VENDOR_WEBGL : context.VENDOR);
  const webglRenderer = context.getParameter(WEBGL_debug_renderer_info ? WEBGL_debug_renderer_info.UNMASKED_RENDERER_WEBGL : context.RENDERER);

  // The different property names are by design; it allows for explicit filtering in analytics.
  if (WEBGL_debug_renderer_info) {
    payload.gpu_webgl2_unmasked_vendor = webglVendor;
    payload.gpu_webgl2_unmasked_renderer = webglRenderer;
  } else {
    payload.gpu_webgl2_vendor = webglVendor;
    payload.gpu_webgl2_renderer = webglRenderer;
  }

  // Fuzzy check: does the WebGPU adapter vendor appear in the WebGL2 strings?
  // e.g., WebGPU vendor "nvidia" should appear in WebGL2 "Google Inc. (NVIDIA)" or renderer "ANGLE (NVIDIA, ...)"
  const webgpuVendor = (info.vendor || '').toLowerCase().trim();
  const webglCombined = `${webglVendor} ${webglRenderer}`.toLowerCase().trim();
  const vendorFoundInWebGL = webgpuVendor.length > 0 && webglCombined.includes(webgpuVendor);

  payload.gpu_webgl2_may_differ_from_webgpu = info.isFallbackAdapter || !vendorFoundInWebGL;

  return payload;
}

export function queryWebGPUDeviceLostPayload(message) {
  const payload = queryCommonWebGPUAdapterPayload();

  const resources = Autodesk.Viewing.Private.OutOfCoreTileManager.getStats().resources;
  const gpuMemoryStats = resources.get('Gpu Memory'); // ResourceType.GPU_MEMORY

  Object.assign(payload, {
    device_lost_message: message,
    gpu_memory_consumed: gpuMemoryStats?.consumed,
    gpu_memory_limit: gpuMemoryStats?.limit
  });
  return payload;
}